<template>
  <DefaultPage>
    <FilterMain
      v-model="filterQuery"
      @input="debouncedSearch"
      @clear="clearInputSearch"
    >
      <FilterBtn
        type="button"
        class="filter-btn"
        :active-filters="activeFilters"
        @click="filterModalVisible = true"
      />
    </FilterMain>

    <div class="applications-view-wrapper">
      <div v-if="isLoadingApps" class="loading-wrapper">
        <BaseLoadingSpinner :with-overlay="false" :size="60" />
      </div>
      <template v-else>
        <section class="applications-wrapper">
          <OvApplicationsLane
            title="Inkommande"
            status="new"
            :applications="applicationsNew"
            :container-style="{
              opacity: 0.5
            }"
            :to-path="toPath"
          ></OvApplicationsLane>

          <OvApplicationsLane
            title="Fatta beslut"
            status="waiting"
            :applications="applicationsWaiting"
            :to-path="toPath"
          ></OvApplicationsLane>

          <OvApplicationsLane
            title="Avslutade"
            status="approved"
            :applications="applicationsApproved"
            :to-path="toPath"
          ></OvApplicationsLane>
        </section>
      </template>
      <router-view />
      <FilterModal
        v-if="filterModalVisible"
        :apps="filteredOvApps"
        :show-swap-filter="false"
        @close="filterModalVisible = false"
      />
    </div>
  </DefaultPage>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import debounce from 'lodash.debounce';

import statusTypes from '@/utils/statusTypes';
import OvApplicationsLane from '@/components/OvApplicationsLane.vue';
import FilterMain from '@/components/FilterMain.vue';
import FilterModal from '@/components/FilterModal.vue';
import BaseLoadingSpinner from '@/components/BaseLoadingSpinner.vue';
import DefaultPage from '@/components/DefaultPage.vue';
import FilterBtn from '@/components/FilterBtn.vue';
import { APP_NAMES, APP_PATHS } from '@/utils/constants';

export default {
  name: 'OvApplicationsView',
  components: {
    OvApplicationsLane,
    FilterMain,
    BaseLoadingSpinner,
    FilterModal,
    DefaultPage,
    FilterBtn
  },
  beforeRouteUpdate(to) {
    this.maybeClearInputSearch(to.path);
  },
  beforeRouteLeave(to) {
    //Leaving to new route and unmounting
    this.maybeClearInputSearch(to.path);
  },
  data() {
    return {
      filterQuery: this.$store.state.applications.filter || '',
      filterModalVisible: false
    };
  },
  computed: {
    ...mapGetters({
      filteredOvApps: 'applications/filteredOvApps',
      activeFilters: 'applications/activeFilters'
    }),
    ...mapState({
      isLoadingApps: state => state.applications.isLoadingApps,
      activeApp: state => state.app.activeApp
    }),
    toPath() {
      return this.activeApp === APP_NAMES.OVERLATELSER_COMPANY
        ? `/${APP_PATHS.OVERLATELSER_COMPANY}/alla`
        : `/${APP_PATHS.OVERLATELSER_PRIVATE}/alla`;
    },
    applicationsNew() {
      if (!this.filteredOvApps) {
        return [];
      }
      return this.filteredOvApps.filter(
        x => x.landlordStatus === statusTypes.INCOMING
      );
    },
    applicationsWaiting() {
      if (!this.filteredOvApps) {
        return [];
      }
      return this.filteredOvApps.filter(
        app => app.landlordStatus === statusTypes.READY
      );
    },
    applicationsApproved() {
      if (!this.filteredOvApps) {
        return [];
      }
      return this.filteredOvApps.filter(app =>
        [statusTypes.APPROVED, statusTypes.ABORTED].includes(app.landlordStatus)
      );
    }
  },
  watch: {
    activeApp(newApp, oldApp) {
      if (newApp !== oldApp) {
        this.fetchOvApps();
      }
    }
  },
  async created() {
    this.debouncedSearch = debounce(this.search, 300);
    await this.fetchOvApps();
    // Ladda arkivet i bakgrunden så vi kan räkna matchningar i andra vyn
    this.fetchOvArchivedApps();
    const firstAppId = this.filteredOvApps?.[0]?.id;
    if (firstAppId) {
      await this.fetchLandlordUsersWithUnassigned(firstAppId);
    }
  },

  methods: {
    ...mapActions({
      fetchOvApps: 'applications/fetchOvApps',
      fetchOvArchivedApps: 'applications/fetchOvArchivedApps',
      fetchLandlordUsersWithUnassigned:
        'applications/fetchLandlordUsersWithUnassigned'
    }),
    ...mapMutations({
      setFilter: 'applications/setFilter'
    }),
    maybeClearInputSearch(path) {
      // Behåll sökningen inom överlåtelser-modulen (alla/arkiv)
      if (
        !path.includes(APP_PATHS.OVERLATELSER_PRIVATE) &&
        !path.includes(APP_PATHS.OVERLATELSER_COMPANY)
      ) {
        this.clearInputSearch();
      }
    },
    clearInputSearch() {
      this.filterQuery = '';
      this.debouncedSearch.cancel();
      this.search('');
    },
    search(query) {
      this.setFilter(query);
    }
  }
};
</script>

<style scoped>
.applications-wrapper {
  display: flex;
  flex-direction: row;
  height: 100%;
  max-width: 1080px;
}

.applications-view-wrapper {
  flex: 1;
  margin-left: 20px;
}

.loading-wrapper {
  position: fixed;
  z-index: 99;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: rgba(0, 0, 0, 0.6);
}
</style>
